import { useReactiveVar } from '@apollo/client';
import { showToast } from 'components/common/CustomToast';
import ParkingContext from 'context/ParkingContext';
import useHasUnlockedCustomerSegment from 'hooks/useHasUnlockedCustomerSegment';
import React, { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { country } from 'states/persistInStorage';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { useSearchStore } from 'stores/searchStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import { Charger, ParkingZoneType } from 'types/common';
import { SegmentProps } from 'types/generatedSchemaTypes';
import ShowMoreButton from '../ShowmoreButton';
import { ChargerSection } from './ChargerSectionHeader';
import SelectParkAndChargeItem from './SelectParkAndChargeItem';

type ChargerSectionItemProps = {
  item: Charger;
  index: number;
  section: ChargerSection;
  activeCustomerSegmentUid: string;
  onChargerSelection?: (chargingStationId: string) => void;
  pricingSegmentsEv: SegmentProps[];
  sections: ChargerSection[];
  showAll: boolean;
  setShowAll: React.Dispatch<React.SetStateAction<boolean>>;
  zoneCode?: string;
  activeType: ParkingZoneType;
};

const RenderChargerItem: React.FC<ChargerSectionItemProps> = ({
  item,
  index,
  section,
  activeCustomerSegmentUid,
  onChargerSelection,
  pricingSegmentsEv,
  sections,
  showAll,
  setShowAll,
  zoneCode,
  activeType,
}) => {
  const { t } = useTranslation();
  const { selectedConnector, setSelectedConnector } = useSearchStore(
    (state) => state
  );

  const { setSelectedEvCharger, selectedEvCharger } =
    useContext(ParkingContext);

  const setChargingTariff = useCallback(
    (chargingTariff?: SegmentProps) => {
      useActiveElementStore.getState().setActiveElementType(activeType);
      useParkzoneStore
        .getState()
        .setSelectedChargingPricingInfoSegment(chargingTariff);
      useParkzoneStore
        .getState()
        .setSelectedChargingPricingInfoSegmentTariffPlanId(
          chargingTariff?.tariffPlanId
        );
    },
    [activeType]
  );

  const handleSetCharger = useCallback(
    (charger: Charger) => {
      setSelectedEvCharger(charger);
      onChargerSelection?.(charger.id);
      const selectedSegment = pricingSegmentsEv.find(
        (segment) => segment.uid === activeCustomerSegmentUid
      );
      setChargingTariff(selectedSegment);
    },
    [
      setSelectedEvCharger,
      onChargerSelection,
      pricingSegmentsEv,
      activeCustomerSegmentUid,
      setChargingTariff,
    ]
  );

  const countryCode = useReactiveVar(country);
  const { isUnlockValid } = useHasUnlockedCustomerSegment(
    section.customerSegmentUid || '',
    countryCode,
    zoneCode
  );
  const isLocked =
    section.customerSegmentAccessType === 'LOCKED' && !isUnlockValid;

  useEffect(() => {
    if (
      selectedConnector &&
      selectedConnector.publicDisplayId === item.publicDisplayId
    ) {
      if (isLocked) {
        showToast(t('errors.segmentLocked'), 'info');
        setSelectedConnector(undefined);
        return;
      }
      handleSetCharger(item);
      setSelectedConnector(undefined);
    }
  }, [
    selectedConnector,
    item,
    isLocked,
    t,
    handleSetCharger,
    setSelectedConnector,
  ]);

  const isFirstSegment =
    sections[0].customerSegmentTitle === section.customerSegmentTitle;
  const isSingleSegment = sections.length === 1;
  const lastItemInCondensedList = 1;
  const notActiveSection =
    activeCustomerSegmentUid !== section.customerSegmentUid;
  const skipChargerRendering =
    isFirstSegment &&
    !showAll &&
    index > lastItemInCondensedList &&
    !isSingleSegment;
  const renderShowAllButton =
    !showAll &&
    !isSingleSegment &&
    isFirstSegment &&
    index === lastItemInCondensedList &&
    section.data.length > 2;

  if (notActiveSection || skipChargerRendering || isLocked) {
    return null;
  }
  return (
    <>
      <StyledWrapper ml={20} mr={20} mb={renderShowAllButton ? 20 : 0}>
        <SelectParkAndChargeItem
          key={`charger-item-${item.id}-${item.evse.id}`}
          isSelected={
            selectedEvCharger?.publicDisplayId === item.publicDisplayId
          }
          onPress={handleSetCharger}
          charger={item}
          testID={`charger-item-${item.id}-${item.evse.id}`}
          lastElement={index === section.data.length - 1}
        />
      </StyledWrapper>
      {renderShowAllButton && (
        <ShowMoreButton onPress={() => setShowAll(true)} />
      )}
    </>
  );
};

export default RenderChargerItem;
